import { useQuery } from "@tanstack/react-query";
import { Fragment, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowsRotate,
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { BaseLogo } from "../../../core/models/entities/BaseLogo";
import { useLogoRepository } from "../../../data/repositories/LogoRepository";
import {
  Dialog_VM,
  DialogFrame,
} from "../Dialogs/dialog-base";
import LoadingElement from "../loading-element/loading-element";
import { LogoStorageCountByOrgDto } from "../../../data/models/responses/LogoDto";
import { useSuccessToast, useErrorToast } from "../Toast/Toast";
import { useTranslation } from "react-i18next";

interface Props {
  logo: BaseLogo;
  dialogOption: Dialog_VM;
}

export function LogoStorageByOrgDialog({ logo, dialogOption }: Props) {
  const { t } = useTranslation();
  const { getLogoStorageCounts, refreshLogoStorageCountForOrg } =
    useLogoRepository();

  const { data, isLoading, isFetching, refetch } = useQuery(
    ["logo-storage-counts", logo.id],
    () => getLogoStorageCounts(logo.id),
    {
      enabled: dialogOption.isOpen,
      staleTime: 0,
      refetchOnWindowFocus: false,
    },
  );

  const sorted = useMemo(
    () => (data ?? []).slice().sort((a, b) =>
      a.organizationName.localeCompare(b.organizationName),
    ),
    [data],
  );

  return (
    <DialogFrame dialogOption={dialogOption}>
      <div className="min-w-[520px] max-w-[760px]">
        <div className="flex items-center justify-between pr-8 mb-4">
          <div>
            <h2 className="text-lg font-semibold">
              {t("compLogoDesign.storageCountsByVendor")}
            </h2>
            <p className="text-xs text-gray-500 mt-1 max-w-[520px]">
              {logo.title}
              {logo.serial ? ` — ${logo.serial}` : ""}
            </p>
          </div>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            title={t("compLogoDesign.reloadCounts")}
            className={`ml-3 px-2 py-1 rounded border border-gray-200 hover:bg-gray-100 text-xs flex items-center gap-1 ${
              isFetching ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
            }`}
          >
            <FontAwesomeIcon
              icon={faArrowsRotate}
              className={isFetching ? "animate-spin" : ""}
            />
            {t("compLogoDesign.reload")}
          </button>
        </div>

        <p className="text-xs text-gray-500 mb-3">
          {t("compLogoDesign.refreshQueuedHint")}
        </p>

        {isLoading ? (
          <div className="h-32 relative">
            <LoadingElement transparent />
          </div>
        ) : sorted.length === 0 ? (
          <div className="text-sm text-gray-500 py-6 text-center">
            {t("compLogoDesign.noVendorStorageCounts")}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-gray-500 border-b">
                <th className="py-2 pr-2 w-6"></th>
                <th className="py-2 pr-2">{t("vendor")}</th>
                <th className="py-2 pr-2">{t("compLogoDesign.billTo")}</th>
                <th className="py-2 pr-2 text-right">
                  {t("compLogoDesign.inStorage")}
                </th>
                <th className="py-2 pr-2 text-right w-16"></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((row) => (
                <StorageCountRow
                  key={row.organizationId}
                  logoId={logo.id}
                  row={row}
                  onRefresh={refreshLogoStorageCountForOrg}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </DialogFrame>
  );
}

interface RowProps {
  logoId: number;
  row: LogoStorageCountByOrgDto;
  onRefresh: (logoId: number, organizationId: number) => Promise<void>;
}

function StorageCountRow({ logoId, row, onRefresh }: RowProps) {
  const { t } = useTranslation();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const hasNumberLogos = row.numberLogoStorageCounts.length > 0;

  const handleRefresh = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await onRefresh(logoId, row.organizationId);
      useSuccessToast(
        t("compLogoDesign.refreshQueuedFor", { name: row.organizationName })
      );
    } catch (error) {
      console.error("Failed to refresh storage count for org:", error);
      useErrorToast(
        t("compLogoDesign.failedToQueueRefreshFor", {
          name: row.organizationName,
        })
      );
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <>
      <tr className="border-b last:border-b-0">
        <td className="py-2 pr-2">
          {hasNumberLogos ? (
            <button
              onClick={() => setIsExpanded((v) => !v)}
              className="w-5 h-5 flex items-center justify-center rounded hover:bg-gray-100"
              title={
                isExpanded
                  ? t("compLogoDesign.collapse")
                  : t("compLogoDesign.expand")
              }
            >
              <FontAwesomeIcon
                icon={isExpanded ? faChevronDown : faChevronRight}
                className="w-3 h-3 text-gray-500"
              />
            </button>
          ) : null}
        </td>
        <td className="py-2 pr-2">
          <div className="font-medium">{row.organizationName}</div>
          <div className="text-xs text-gray-500">{row.organizationNavNumber}</div>
        </td>
        <td className="py-2 pr-2 text-xs text-gray-600">
          {row.parentOrganizationNavNumber ?? row.organizationNavNumber}
        </td>
        <td className="py-2 pr-2 text-right font-semibold tabular-nums">
          {Math.max(0, row.storageCount)}
        </td>
        <td className="py-2 pr-2 text-right">
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className={`p-1 rounded hover:bg-gray-100 ${
              isRefreshing ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
            }`}
            title={t("compLogoDesign.refreshStorageCountFor", {
              name: row.organizationName,
            })}
          >
            <FontAwesomeIcon
              icon={faArrowsRotate}
              className={`w-3 h-3 ${isRefreshing ? "animate-spin" : ""}`}
            />
          </button>
        </td>
      </tr>
      {isExpanded && hasNumberLogos && (
        <tr className="bg-gray-50">
          <td></td>
          <td colSpan={4} className="py-2 pr-2">
            <div className="grid grid-cols-[40px_1fr] gap-y-1 max-w-[260px]">
              <div className="text-xs uppercase text-gray-500">
                {t("number")}
              </div>
              <div className="text-xs uppercase text-gray-500 text-right pr-2">
                {t("compLogoDesign.inStorage")}
              </div>
              {row.numberLogoStorageCounts.map((nl) => (
                <Fragment key={nl.value}>
                  <div className="text-sm">{nl.value}</div>
                  <div className="text-sm text-right pr-2 tabular-nums">
                    {Math.max(0, nl.inStorage)}
                  </div>
                </Fragment>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
